import { useState, useEffect } from "react";
import { Bolt, MoveArrowRight } from "@vibe/icons";
import { EXTERNAL_LINKS } from "@data/externalLinks";

type AnimationStep = "field" | "value" | "board" | "complete";

const FIELD_OPTIONS = ["Owner", "Priority", "Status"];
const STATUS_OPTIONS = ["Not Started", "Working on it", "Stuck", "Done"];
const BOARD_OPTIONS = ["Product Roadmap", "Marketing Hub", "Sales Pipeline", "Design System"];

export function AutomationBanner() {
  const [step, setStep] = useState<AnimationStep>("field");
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  const [currentValueIndex, setCurrentValueIndex] = useState(0);
  const [currentBoardIndex, setCurrentBoardIndex] = useState(0);
  const [selectedField, setSelectedField] = useState("");
  const [selectedValue, setSelectedValue] = useState("");
  const [selectedBoard, setSelectedBoard] = useState("");

  useEffect(() => {
    const timeoutIds: number[] = [];

    const runAnimation = () => {
      const timeline = [
        // Step 1: Cycle through field options
        {
          delay: 1000,
          action: () => {
            setStep("field");
            setCurrentFieldIndex(0);
          },
        },
        {
          delay: 1600,
          action: () => {
            setCurrentFieldIndex(1);
          },
        },
        {
          delay: 2200,
          action: () => {
            setCurrentFieldIndex(2);
            setSelectedField("Status");
          },
        },

        // Step 2: Cycle through value options
        {
          delay: 3400,
          action: () => {
            setStep("value");
            setCurrentValueIndex(0);
          },
        },
        {
          delay: 4000,
          action: () => {
            setCurrentValueIndex(1);
          },
        },
        {
          delay: 4600,
          action: () => {
            setCurrentValueIndex(2);
          },
        },
        {
          delay: 5200,
          action: () => {
            setCurrentValueIndex(3);
            setSelectedValue("Done");
          },
        },

        // Step 3: Cycle through board options
        {
          delay: 6400,
          action: () => {
            setStep("board");
            setCurrentBoardIndex(0);
          },
        },
        {
          delay: 7000,
          action: () => {
            setCurrentBoardIndex(1);
          },
        },
        {
          delay: 7600,
          action: () => {
            setCurrentBoardIndex(2);
            setSelectedBoard("Sales Pipeline");
          },
        },

        // Complete state
        {
          delay: 8600,
          action: () => {
            setStep("complete");
          },
        },

        // Reset and loop
        {
          delay: 12000,
          action: () => {
            setStep("field");
            setSelectedField("");
            setSelectedValue("");
            setSelectedBoard("");
            setCurrentFieldIndex(0);
            setCurrentValueIndex(0);
            setCurrentBoardIndex(0);
          },
        },

        // Restart animation
        { delay: 12500, action: () => runAnimation() },
      ];

      timeline.forEach(({ delay, action }) => {
        timeoutIds.push(window.setTimeout(action, delay));
      });
    };

    runAnimation();

    return () => {
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
    };
  }, []);

  const renderSelector = (
    options: string[],
    currentIndex: number,
    selected: string,
    placeholder: string,
    isAnimating: boolean,
    minWidth: string,
  ) => {
    const hasSelection = selected !== "";
    const displayValue = hasSelection
      ? selected
      : isAnimating
        ? options[currentIndex]
        : placeholder;
    const borderColor = hasSelection
      ? "rgba(0, 200, 117, 0.6)"
      : isAnimating
        ? "rgba(139, 92, 246, 0.4)"
        : "rgba(148, 163, 184, 0.3)";

    return (
      <span className="inline-block relative" style={{ minWidth }}>
        <span
          className="inline-block px-2 py-1 rounded-md transition-all duration-300"
          style={{
            borderBottom: `2px solid ${borderColor}`,
            background:
              isAnimating && !hasSelection
                ? "rgba(255, 255, 255, 0.4)"
                : hasSelection
                  ? "rgba(255, 255, 255, 0.45)"
                  : "rgba(255, 255, 255, 0.25)",
            color: hasSelection ? "#00c875" : isAnimating ? "#1e293b" : "#94a3b8",
            fontWeight: hasSelection ? 600 : isAnimating ? 500 : 400,
            minWidth,
            display: "inline-block",
            textAlign: "center",
            boxShadow: hasSelection
              ? "0 2px 8px rgba(0, 200, 117, 0.15)"
              : isAnimating
                ? "0 2px 8px rgba(139, 92, 246, 0.1)"
                : "none",
          }}
        >
          <span
            className="inline-block"
            style={{
              animation: isAnimating && !hasSelection ? "valueSlide 0.3s ease-out" : "none",
            }}
          >
            {displayValue}
          </span>
        </span>
      </span>
    );
  };

  return (
    <div
      className="px-6 py-3 mt-3 mb-5 border border-[#e6e9ef] rounded-xl flex-shrink-0 flex justify-between automation-banner h-[70px]"
      style={{
        position: "relative",
        zIndex: 10,
      }}
    >
      <style>{`
        @keyframes valueSlide {
          0% {
            opacity: 0;
            transform: translateY(-8px);
          }
          50% {
            opacity: 0.5;
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      <div className="flex gap-3 items-center min-w-0">
        <div className="flex items-center justify-center bg-[#A78BFA26] rounded-md h-9 w-9">
          <Bolt size={18} color="#A78BFA" />
        </div>
        <p className="text-sm text-[#323338] mb-0">
          When{"  "}
          {renderSelector(
            FIELD_OPTIONS,
            currentFieldIndex,
            selectedField,
            "field",
            step === "field",
            "92px",
          )}
          {"  "}
          changes to{"  "}
          {renderSelector(
            STATUS_OPTIONS,
            currentValueIndex,
            selectedValue,
            "value",
            step === "value",
            "118px",
          )}
          {"  "}
          share to{"  "}
          {renderSelector(
            BOARD_OPTIONS,
            currentBoardIndex,
            selectedBoard,
            "board",
            step === "board",
            "146px",
          )}
        </p>
      </div>

      <div className="flex gap-[9px] items-center group">
        <a
          href={EXTERNAL_LINKS.AUTOMATIONS}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-[#0073EA] cursor-pointer no-underline font-medium "
        >
          Explore templates
        </a>
        <MoveArrowRight
          size={14}
          color="#0073EA"
          className="transition-transform duration-300 group-hover:animate-bounce-horizontal"
        />
      </div>
    </div>
  );
}
